import { assert, defined, definedMap, mapFilterUndefined } from "@glideapps/ts-necessities";
import fromPairs from "lodash/fromPairs";
import startCase from "lodash/startCase";

import { ArgumentSpec, DeserializationSpec } from "../../components/computation-types";
import { ReactUI } from "../../components/uis";
import {
    ArrayContentDescription,
    ArrayScreenFormat,
    ColumnAssignmentsDescription,
    ComponentDescription,
    ContainerComponentDescription,
    Description,
    getTablesWithPrimitiveColumns,
    InputOutputTables,
    makeInputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    PropertyKind,
    TableColumn,
    TableGlideType,
} from "../description";
import {
    makeImagePropertyDescriptor,
    makePropertyDescriptorsForColumns,
    makeTextPropertyDescriptor,
} from "./descriptor-utils";
import { SimpleComponentDescription, SimpleComponentHandler } from "./simple-handler";
import { EnumPropertyHandler, SwitchPropertyHandler } from "./property-handlers";
import { PropertySection, SuperPropertySection } from "./property-sections";
import {
    PropertyDescriptor,
    WireRowComponentHydrator,
    EnumPropertyCase,
    WireInflationBackend,
    ComponentBuilder,
    WireTableComponentHydrator,
    ComponentSpecialCaseDescriptor,
    PropertyTableGetter,
    ComponentConfiguratorContext,
    ArrayPropertyStyle,
    ComponentHandler,
    ComponentDescriptor,
    SchemaInspector,
    ActionPropertyDescriptor,
    WireValueGetter,
    WireRowComponentHydrationBackend,
    WireActionHydrator,
    InteractiveComponentConfiguratorContext,
    RewritingComponentConfiguratorContext,
    WireRowComponentInflationBackend,
    EnumVisual,
} from ".";
import { asBoolean, asMaybeString, GroundValue, Row } from "../../computation-model/data";
import { ArrayContentHandler, ArrayContentHandlerBase } from "../array-screens/array-content";
import { AppKind, SerializedComponent } from "../../components/SerializedApp";
import { registerActionRunner, inflateActions, inflateColumnAssignments, inflateComponent } from "../../wire/utils";
import { WireAction, WireComponent, WireEditableValue, WireListComponent, WireListComponentFormat } from "../../wire";
import { ComponentHandlerBase } from "./handler";
import { UIClass } from "../../components/types";
import { getActionProperty, getArrayProperty, getTableProperty } from "./property-utils";
import { getColumnAssignments } from "../description-utils";
import { getDefaultPrimitiveActionKinds } from "./primitive";
import { makeRowID } from "../../make-row-id";
import { TableName } from "../../database-strings";
import { WireComponentKind } from "../../wire/component-kind";
import type { InlineListComponentDescription } from "./inline-list";
import { mapFilterUndefinedAsync } from "../../Support";
import { duplicateComponent, handlerForComponentKind } from "../handlers";

type PropertyDescriptorConstructor = (
    desc: ComponentDescription | ArrayContentDescription | undefined,
    parentDesc: ComponentDescription | ArrayContentDescription | undefined,
    getPropertyTable: PropertyTableGetter<any> | undefined,
    schema: SchemaInspector | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined,
    isEditedInApp: boolean
) => PropertyDescriptor | undefined;

type WithEnumPropertyValuesConvenient<TEnum extends string> = readonly TEnum[] | EnumPropertyCase<TEnum>[];

function finalizeWithEnumPropertyValues<TEnum extends string>(
    values: WithEnumPropertyValuesConvenient<TEnum>
): EnumPropertyCase<TEnum>[] {
    if (typeof values[0] === "string") {
        return (values as TEnum[]).map(value => ({ value, label: startCase(value) }));
    }
    return values as EnumPropertyCase<TEnum>[];
}

export type PropertiesOfComponent<T> = T extends FluentComponent<infer U, infer V> ? U & V : never;
type PropertiesOfArrayContent<T> = T extends FluentArrayContent<infer U, infer V> ? U & V : never;

type ToPropertyDescriptionOrUndefined<T> = { readonly [K in keyof T]: PropertyDescription | undefined };
export type DescriptionOfProperties<T> = T extends FluentProperties<infer U, unknown>
    ? ToPropertyDescriptionOrUndefined<U>
    : never;
// TODO: We're not forcing the correct `kind` here.  We'd have to add another
// generic argument to `FluentComponent` to be the component kind, then we
// could use that here as the `kind` type.
export type DescriptionOfComponent<T> = ComponentDescription &
    {
        readonly [K in keyof PropertiesOfComponent<T>]?: PropertyDescription;
    };

type ArrayContentDescriptionForProperties<T> = ArrayContentDescription &
    { [K in keyof T]: PropertyDescription | undefined };
//  type DescriptionOfArrayContent<T> = T extends FluentArrayContent<infer U>
//     ? ArrayContentDescriptionForProperties<U>
//     : never;

export type DescriptionOfArrayContentInlineList<T> = InlineListComponentDescription &
    {
        readonly [K in keyof PropertiesOfArrayContent<T>]?: PropertyDescription;
    };

interface PropertyOptions {
    readonly displayName: string;
    readonly preferredNames: readonly string[];
    readonly required: boolean;
    readonly propertySection?: SuperPropertySection;
}

interface WhenOption<T, TParent> {
    readonly when?: (
        desc: ToPropertyDescriptionOrUndefined<T>,
        parentDesc: ToPropertyDescriptionOrUndefined<TParent> | undefined
    ) => boolean;
}

interface ConstructableSpec {
    readonly descriptorConstructor: PropertyDescriptorConstructor;
}

interface SpecBase extends ConstructableSpec {
    readonly argumentSpec: ArgumentSpec;
}

type ValueConverter = (v: GroundValue) => GroundValue;

type ValueGetterGetter<T> = (desc: T, ib: WireInflationBackend) => WireValueGetter;

interface PropertySpec<T> extends SpecBase {
    readonly isSearchable: boolean;
    readonly getValueGetter: ValueGetterGetter<T>;
    readonly converter: ValueConverter;
}

interface ArraySpec extends ConstructableSpec {
    readonly name: string;
    readonly itemsSpec: FluentPropertiesSpec;
}

interface ActionSpec extends SpecBase {}

interface FluentPropertiesSpec {
    readonly propertySpecs: readonly PropertySpec<any>[];
    readonly arraySpecs: readonly ArraySpec[];
    readonly actionSpecs: readonly ActionSpec[];
}

function ensurePropertyOptions(propertyName: string, opts: Partial<PropertyOptions>): PropertyOptions {
    const { displayName = startCase(propertyName), preferredNames = [], required = false } = opts;
    return {
        displayName,
        preferredNames,
        required,
        propertySection: opts.propertySection,
    };
}

function makePropertyDescriptors(
    desc: ComponentDescription | ArrayContentDescription | undefined,
    parentDesc: ComponentDescription | ArrayContentDescription | undefined,
    specs: readonly ConstructableSpec[],
    getPropertyTable: PropertyTableGetter<any> | undefined,
    schema: SchemaInspector | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined,
    isEditedInApp: boolean
): readonly PropertyDescriptor[] {
    return mapFilterUndefined(specs, p =>
        p.descriptorConstructor(desc, parentDesc, getPropertyTable, schema, mutatingScreenKind, isEditedInApp)
    );
}

function makeActionDescriptors<T extends Description>(
    desc: ComponentDescription | ArrayContentDescription | undefined,
    parentDesc: ComponentDescription | ArrayContentDescription | undefined,
    specs: readonly ConstructableSpec[],
    getPropertyTable: PropertyTableGetter<any> | undefined,
    schema: SchemaInspector | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined
): readonly ActionPropertyDescriptor<T>[] {
    return makePropertyDescriptors(
        desc,
        parentDesc,
        specs,
        getPropertyTable,
        schema,
        mutatingScreenKind,
        false
    ) as readonly ActionPropertyDescriptor<T>[];
}

export class FluentProperties<TProps, TParent> {
    constructor(public readonly spec: FluentPropertiesSpec) {}

    public withParent<T>(): FluentProperties<TProps, T> {
        // Ugh.  The alternative to this would be to always construct it with
        // the parent type, but then how do we make sure that the `spec`
        // (which would have to be optional in that case) is of the correct
        // type?
        assert(
            this.spec.propertySpecs.length === 0 &&
                this.spec.arraySpecs.length === 0 &&
                this.spec.actionSpecs.length === 0
        );
        return this as FluentProperties<TProps, T>;
    }

    private withArgument<T>(
        argumentSpec: ArgumentSpec,
        descriptorConstructor: PropertyDescriptorConstructor,
        isSearchable: boolean,
        getValueGetter: ValueGetterGetter<ToPropertyDescriptionOrUndefined<TProps>>,
        converter: (v: GroundValue) => GroundValue,
        when?: (
            desc: ToPropertyDescriptionOrUndefined<TProps>,
            parentDesc: ToPropertyDescriptionOrUndefined<TParent> | undefined
        ) => boolean
    ): FluentProperties<T, TParent> {
        let c = descriptorConstructor;
        if (when !== undefined) {
            c = (desc, parentDesc, ...args) => {
                if (desc === undefined || !when(desc as any, parentDesc as any)) return undefined;
                return descriptorConstructor(desc, parentDesc, ...args);
            };
        }
        return new FluentProperties({
            ...this.spec,
            propertySpecs: [
                ...this.spec.propertySpecs,
                { descriptorConstructor: c, argumentSpec, isSearchable, getValueGetter, converter },
            ],
        });
    }

    private makeDefaultValueGetterGetter<T>(name: string, withFormat: boolean): ValueGetterGetter<T> {
        return (desc, ib) => ib.getValueGetterForProperty((desc as any)[name], withFormat)[0];
    }

    public withText<T extends string>(
        name: T,
        opts: Partial<
            PropertyOptions &
                WhenOption<TProps, TParent> & { readonly allowColumn: boolean; readonly isEditedInApp: boolean }
        > = {}
    ): FluentProperties<TProps & { readonly [K in T]?: string | undefined }, TParent> {
        const { displayName, preferredNames, required, propertySection } = ensurePropertyOptions(name, opts);
        const argumentSpec: ArgumentSpec = [name, "evaluable", required];
        const propertyDescriptorConstructor: PropertyDescriptorConstructor = (
            _desc,
            _parentDesc,
            getIndirectTable,
            _schema,
            mutatingScreenKind
        ) =>
            makeTextPropertyDescriptor(name, displayName, "", required, mutatingScreenKind, {
                preferredType: "string",
                preferredNames,
                searchable: true,
                columnFirst: false,
                propertySection,
                getIndirectTable,
                allowColumn: opts.allowColumn,
                isEditedInApp: opts.isEditedInApp,
            });
        return this.withArgument(
            argumentSpec,
            propertyDescriptorConstructor,
            true,
            this.makeDefaultValueGetterGetter(name, true),
            asMaybeString,
            opts.when
        );
    }

    public withURI<T extends string>(
        name: T,
        opts: Partial<PropertyOptions & WhenOption<TProps, TParent>> = {}
    ): FluentProperties<TProps & { readonly [K in T]?: string | undefined }, TParent> {
        const { displayName, preferredNames, required, propertySection } = ensurePropertyOptions(name, {
            preferredNames: ["video", "url"],
            ...opts,
        });
        const argumentSpec: ArgumentSpec = [name, "evaluable", required];
        const propertyDescriptorConstructor: PropertyDescriptorConstructor = (
            _desc,
            _parentDesc,
            getIndirectTable,
            _schema,
            mutatingScreenKind
        ) =>
            makeTextPropertyDescriptor(name, displayName, "", required, mutatingScreenKind, {
                preferredType: "uri",
                preferredNames,
                searchable: false,
                columnFirst: false,
                getIndirectTable,
                propertySection,
            });
        return this.withArgument(
            argumentSpec,
            propertyDescriptorConstructor,
            false,
            this.makeDefaultValueGetterGetter(name, false),
            asMaybeString,
            opts.when
        );
    }

    public withImage<T extends string>(
        name: T,
        opts: Partial<PropertyOptions & WhenOption<TProps, TParent>> = {}
    ): FluentProperties<TProps & { readonly [K in T]?: string | undefined }, TParent> {
        const { displayName, required, propertySection } = ensurePropertyOptions(name, opts);

        const argumentSpec: ArgumentSpec = [name, "evaluable", required];
        const propertyDescriptorConstructor: PropertyDescriptorConstructor = (
            _desc,
            _parentDesc,
            getIndirectTable,
            _schema,
            mutatingScreenKind
        ) =>
            makeImagePropertyDescriptor(
                name,
                displayName,
                "Enter URL",
                required,
                false,
                false,
                getIndirectTable,
                mutatingScreenKind,
                propertySection
            );
        return this.withArgument(
            argumentSpec,
            propertyDescriptorConstructor,
            false,
            this.makeDefaultValueGetterGetter(name, false),
            asMaybeString,
            opts.when
        );
    }

    public withEnum<T extends string, TEnum extends string>(
        name: T,
        values: WithEnumPropertyValuesConvenient<TEnum>,
        opts: Partial<
            PropertyOptions & WhenOption<TProps, TParent> & { defaultValue: TEnum } & { enumVisual?: EnumVisual }
        > = {}
    ): FluentProperties<TProps & { readonly [K in T]: TEnum }, TParent> {
        const { displayName, required, propertySection } = ensurePropertyOptions(name, opts);

        const enumValues = finalizeWithEnumPropertyValues(values);
        const { defaultValue = enumValues[0].value } = opts;

        const argumentSpec: ArgumentSpec = [name, "evaluable", required];
        const descr = new EnumPropertyHandler(
            { [name]: defaultValue },
            displayName,
            displayName,
            enumValues,
            propertySection ?? PropertySection.Options,
            opts.enumVisual ?? "dropdown"
        );
        const propertyDescriptorConstructor: PropertyDescriptorConstructor = () => descr;
        return this.withArgument(
            argumentSpec,
            propertyDescriptorConstructor,
            false,
            desc => () => descr.getEnum(desc as any),
            asMaybeString,
            opts.when
        );
    }

    public withSwitch<T extends string>(
        name: T,
        defaultValue: boolean,
        opts: Partial<PropertyOptions & WhenOption<TProps, TParent>> = {}
    ): FluentProperties<TProps & { readonly [K in T]: boolean }, TParent> {
        const { displayName, propertySection } = ensurePropertyOptions(name, opts);

        const argumentSpec: ArgumentSpec = [name, "evaluable", true];
        const descr = new SwitchPropertyHandler(
            { [name]: defaultValue },
            displayName,
            propertySection ?? PropertySection.Options
        );
        const propertyDescriptorConstructor: PropertyDescriptorConstructor = () => descr;
        return this.withArgument(
            argumentSpec,
            propertyDescriptorConstructor,
            false,
            desc => () => descr.getSwitch(desc as any),
            asBoolean,
            opts.when
        );
    }

    public withArray<T extends string, P>(
        name: T,
        props: FluentProperties<P, TProps>,
        opts: Partial<PropertyOptions> = {}
    ): FluentProperties<TProps & { readonly [K in T]: readonly P[] }, TParent> {
        const { displayName, propertySection } = ensurePropertyOptions(name, opts);

        const propertyDescriptorConstructor: PropertyDescriptorConstructor = (
            desc,
            _parentDesc,
            getIndirectTable,
            schema,
            mutatingScreenKind,
            isEditedInApp
        ) => ({
            kind: PropertyKind.Array,
            label: displayName,
            property: { name },
            section: propertySection ?? PropertySection.Options,
            properties: [
                ...makePropertyDescriptors(
                    // FIXME: get the array properties out of `desc`
                    undefined,
                    desc,
                    props.spec.propertySpecs,
                    getIndirectTable,
                    schema,
                    mutatingScreenKind,
                    isEditedInApp
                ),
                ...makeActionDescriptors(
                    // FIXME: get the array properties out of `desc`
                    undefined,
                    desc,
                    props.spec.actionSpecs,
                    getIndirectTable,
                    schema,
                    mutatingScreenKind
                ),
            ],
            allowEmpty: false,
            allowReorder: true,
            addItemLabel: "Add Item",
            style: ArrayPropertyStyle.KeyValue,
        });
        return new FluentProperties({
            ...this.spec,
            arraySpecs: [
                ...this.spec.arraySpecs,
                {
                    name,
                    descriptorConstructor: propertyDescriptorConstructor,
                    itemsSpec: props.spec,
                },
            ],
        });
    }

    public withAction<T extends string>(
        name: T,
        opts: Partial<PropertyOptions> = {}
    ): FluentProperties<TProps & { readonly [K in T]?: WireAction | undefined }, TParent> {
        const { displayName, required, propertySection } = ensurePropertyOptions(name, opts);
        const argumentSpec: ArgumentSpec = [name, "action", required];
        const descriptorConstructor: PropertyDescriptorConstructor = (
            _desc,
            _parentDesc,
            getIndirectTable,
            schema,
            mutatingScreenKind
        ) => {
            const kinds = getDefaultPrimitiveActionKinds(schema, mutatingScreenKind);
            return {
                kind: PropertyKind.Action,
                property: { name },
                label: displayName,
                required,
                kinds,
                defaultAction: required,
                getIndirectTable,
                section: propertySection ?? PropertySection.Action,
            };
        };
        return new FluentProperties({
            ...this.spec,
            actionSpecs: [...this.spec.actionSpecs, { argumentSpec, descriptorConstructor }],
        });
    }
}

interface FluentComponentSpec {
    readonly displayName?: string;
    readonly description?: string;
    readonly kind?: WireComponentKind;
    readonly group?: string;
    readonly icon?: string;
    readonly isContainer: boolean;
    readonly properties: FluentPropertiesSpec;
    readonly formProperties: FluentPropertiesSpec;
    readonly actions?: readonly ActionSpec[];
}

interface Getters {
    readonly valueGetters: readonly (readonly [string, WireValueGetter, ValueConverter])[];
    readonly arrayBuilders: readonly (readonly [
        string,
        (hb: WireRowComponentHydrationBackend) => readonly unknown[]
    ])[];
    readonly actionHydrators: readonly (readonly [string, WireActionHydrator])[];
}

function makeGetters<T extends Description>(
    ib: WireInflationBackend,
    { propertySpecs, arraySpecs, actionSpecs }: FluentPropertiesSpec,
    desc: T
): Getters {
    const valueGetters = propertySpecs.map(p => {
        const name = p.argumentSpec[0];
        return [name, p.getValueGetter(desc, ib), p.converter] as const;
    });

    const arrayBuilders = arraySpecs.map(a => {
        const arr: readonly Description[] = getArrayProperty((desc as any)[a.name]) ?? [];
        const itemGetters = arr.map(item => makeGetters(ib, a.itemsSpec, item));
        return [
            a.name,
            (hb: WireRowComponentHydrationBackend) => itemGetters.map(getters => hydrateGetters(getters, hb)),
        ] as const;
    });

    const actionHydrators = mapFilterUndefined(actionSpecs ?? [], a => {
        const name = a.argumentSpec[0];
        const actionDesc = getActionProperty((desc as any)[name]);
        if (actionDesc === undefined) return undefined;

        const runner = inflateActions(ib, [actionDesc]);
        if (runner === undefined) return undefined;

        return [name, runner] as const;
    });

    return { valueGetters, arrayBuilders, actionHydrators };
}

function hydrateGetters<T>(
    { valueGetters, arrayBuilders, actionHydrators }: Getters,
    hb: WireRowComponentHydrationBackend
): T {
    return {
        ...fromPairs(valueGetters.map(([k, g, c]) => [k, c(g(hb))])),
        ...fromPairs(arrayBuilders.map(([k, b]) => [k, b(hb)])),
        ...fromPairs(
            mapFilterUndefined(actionHydrators, ([k, h]) => {
                const runner = h(hb);
                if (runner === undefined) return undefined;
                return [k, registerActionRunner(hb, runner)];
            })
        ),
    } as any;
}

function makeComponentHandler(spec: FluentComponentSpec, uiClass: UIClass): SimpleComponentHandler {
    const {
        properties: { propertySpecs, arraySpecs, actionSpecs },
        displayName,
        kind,
        group = "Layout",
        description = "No description",
        icon = "componentTitle",
        isContainer,
    } = spec;
    assert(displayName !== undefined && kind !== undefined);

    class Handler extends SimpleComponentHandler {
        constructor() {
            super({
                kind,
                name: defined(displayName),
                group,
                description,
                img: icon,
                uiClass,
                helpPath: "videoPlayer",
            });
        }

        // FIXME: Implement `needsColumns`

        public get appKinds(): AppKind | "both" {
            return AppKind.Page;
        }

        public get supportsSubComponents(): boolean {
            return isContainer;
        }

        private getComponents(desc: SimpleComponentDescription): readonly ComponentDescription[] | undefined {
            if (!isContainer) return undefined;
            // TODO: This is really ugly.
            return (desc as unknown as ContainerComponentDescription).components ?? [];
        }

        protected getPropertyDescriptors(
            desc: SimpleComponentDescription,
            mutatingScreenKind: MutatingScreenKind | undefined,
            schema: SchemaInspector | undefined
        ) {
            return makePropertyDescriptors(
                desc,
                undefined,
                [...propertySpecs, ...arraySpecs],
                undefined,
                schema,
                mutatingScreenKind,
                false
            );
        }

        public getActionDescriptors(
            desc: SimpleComponentDescription,
            _table: TableGlideType | undefined,
            schema: SchemaInspector | undefined,
            mutatingScreenKind: MutatingScreenKind | undefined
        ): readonly ActionPropertyDescriptor<SimpleComponentDescription>[] {
            return makeActionDescriptors(desc, undefined, actionSpecs ?? [], undefined, schema, mutatingScreenKind);
        }

        public async duplicateComponent(
            desc: SimpleComponentDescription,
            copyTables: InputOutputTables | undefined,
            iccc: InteractiveComponentConfiguratorContext,
            mutatingScreenKind: MutatingScreenKind | undefined,
            screensCreated: Set<string>
        ): Promise<SimpleComponentDescription> {
            const base = await super.duplicateComponent(desc, copyTables, iccc, mutatingScreenKind, screensCreated);
            const oldComponents = this.getComponents(desc);
            if (oldComponents === undefined) {
                return base;
            }

            const components = await mapFilterUndefinedAsync(oldComponents, async c =>
                duplicateComponent(c, copyTables, iccc, mutatingScreenKind, screensCreated)
            );
            return { ...base, components } as SimpleComponentDescription;
        }

        public rewriteAfterReload(
            desc: SimpleComponentDescription,
            tables: InputOutputTables,
            ccc: RewritingComponentConfiguratorContext,
            mutatingScreenKind: MutatingScreenKind | undefined,
            isRewrite: boolean
        ): SimpleComponentDescription | undefined {
            const newDesc = super.rewriteAfterReload(desc, tables, ccc, mutatingScreenKind, isRewrite);
            if (newDesc === undefined) return undefined;
            const oldComponents = this.getComponents(desc);
            if (oldComponents === undefined) {
                return newDesc;
            }

            const components = mapFilterUndefined(oldComponents ?? [], c => {
                const handler = handlerForComponentKind(c.kind);
                if (handler === undefined) return undefined;
                return handler.rewriteAfterReload(c, tables, ccc, mutatingScreenKind, isRewrite);
            });

            return { ...newDesc, components } as SimpleComponentDescription;
        }

        public inflate(
            ib: WireRowComponentInflationBackend,
            desc: SimpleComponentDescription
        ): WireRowComponentHydrator | undefined {
            const getters = makeGetters(ib, spec.properties, desc);
            const components = this.getComponents(desc);
            const hydrators = definedMap(components, cs => mapFilterUndefined(cs, c => inflateComponent(ib, c)?.[0]));

            return hb => {
                let hydrated: WireComponent = { kind, ...hydrateGetters<any>(getters, hb) };
                if (hydrators !== undefined) {
                    hydrated = { ...hydrated, components: mapFilterUndefined(hydrators, h => h(hb)) } as WireComponent;
                }
                return hydrated;
            };
        }
    }

    return new Handler();
}

interface FormComponentDescription extends ColumnAssignmentsDescription, ComponentDescription {
    readonly targetTable: PropertyDescription | undefined;
}

function makeFormComponentHandler<TDesc extends FormComponentDescription>(
    spec: FluentComponentSpec
): ComponentHandler<TDesc> {
    const {
        properties: { propertySpecs },
        formProperties: { propertySpecs: formPropertySpecs },
        displayName,
        kind,
        group = "Forms",
        description = "No description",
        icon = "componentTitle",
    } = spec;
    assert(kind !== undefined && displayName !== undefined);
    assert(!spec.isContainer);

    class Handler extends ComponentHandlerBase<TDesc> {
        constructor() {
            super(defined(kind));
        }

        public get appKinds(): AppKind | "both" {
            return AppKind.Page;
        }

        private getTargetTableName(desc: TDesc | undefined): TableName | undefined {
            return getTableProperty(desc?.targetTable);
        }

        private makeTargetPropertyDescriptors(
            targetTableName: TableName | undefined,
            inputTable: TableGlideType | undefined,
            schema: SchemaInspector
        ): [readonly PropertyDescriptor[], readonly TableColumn[]] {
            const targetTable = definedMap(targetTableName, n => schema.findTable(n));
            if (targetTableName === undefined || inputTable === undefined) return [[], []];

            const cases: EnumPropertyCase<string>[] = formPropertySpecs.map(p => {
                const name = p.argumentSpec[0];
                return {
                    value: name,
                    label: startCase(name),
                };
            });

            return makePropertyDescriptorsForColumns<TDesc>(
                makeInputOutputTables(inputTable, targetTable),
                false,
                false,
                true,
                true,
                getColumnAssignments,
                columns => ({ columnAssignments: columns } as Partial<TDesc>),
                {
                    kind: PropertyKind.Enum,
                    menuLabel: "Fields",
                    cases,
                    visual: "dropdown",
                }
            );
        }

        public getDescriptor(
            desc: TDesc | undefined,
            table: TableGlideType | undefined,
            ccc: ComponentConfiguratorContext,
            mutatingScreenKind: MutatingScreenKind | undefined
        ): ComponentDescriptor<TDesc> {
            const targetTableName = this.getTargetTableName(desc);
            const [columnAssignmentDescriptors] = this.makeTargetPropertyDescriptors(targetTableName, table, ccc);
            return {
                name: defined(displayName),
                description,
                img: icon,
                group,
                helpUrl: "FIXME: doc URL!",
                properties: [
                    ...this.getBasePropertyDescriptors(),
                    ...makePropertyDescriptors(
                        desc,
                        undefined,
                        propertySpecs,
                        undefined,
                        ccc,
                        mutatingScreenKind,
                        false
                    ),
                    {
                        kind: PropertyKind.Table,
                        property: { name: "targetTable" },
                        label: "Sheet",
                        required: true,
                        getAllowedTables: (_d, schema) => getTablesWithPrimitiveColumns(schema.tables),
                        section: PropertySection.Data,
                    },
                    ...columnAssignmentDescriptors,
                ],
            };
        }

        public inflate?(ib: WireInflationBackend, desc: TDesc): WireRowComponentHydrator | undefined {
            const targetTableName = getTableProperty(desc?.targetTable);
            if (targetTableName === undefined) return undefined;

            const getters = makeGetters(ib, spec.properties, desc);
            const [, targetColumns] = this.makeTargetPropertyDescriptors(targetTableName, ib.tables.input, ib);
            const targetColumnNames = new Set(targetColumns.map(c => c.name));
            const columnAssignments = getColumnAssignments(desc).filter(a => targetColumnNames.has(a.destColumn));

            function getFormValue(hb: WireRowComponentHydrationBackend, name: string): WireEditableValue<string> {
                return hb.getState(name, (x): x is string => typeof x === "string", "");
            }

            const formPropertyNames = new Set<string>();
            const outputValueGetters = inflateColumnAssignments(ib, targetTableName, columnAssignments, true, value => {
                if (typeof value !== "string") return undefined;
                formPropertyNames.add(value);
                return hb => getFormValue(hb, value).value;
            });
            if (outputValueGetters === undefined) return undefined;

            return hb => {
                const formProps: [string, WireEditableValue<string>][] = Array.from(formPropertyNames).map(name => {
                    const ev = getFormValue(hb, name);
                    return [name, ev];
                });
                const outputRow: Row = {
                    $rowID: makeRowID(),
                    $isVisible: false,
                    ...fromPairs(outputValueGetters.map(([n, g]) => [n, g(hb)])),
                };
                const submitAction: WireAction = {
                    token: hb.registerAction(ab => ab.addRow(targetTableName, outputRow)),
                };
                return {
                    kind,
                    submitAction,
                    ...hydrateGetters<any>(getters, hb),
                    ...fromPairs(formProps),
                } as WireComponent;
            };
        }
    }

    return new Handler();
}

export class FluentComponent<TProps, TForm> {
    constructor(private readonly spec: FluentComponentSpec) {}

    public makeUI(componentName: string): UIClass {
        const {
            properties: { propertySpecs },
        } = this.spec;
        class ComponentUI extends ReactUI {
            static get spec(): DeserializationSpec<"ui"> {
                return [componentName, "ui", ...propertySpecs.map(p => p.argumentSpec)];
            }
        }
        return ComponentUI;
    }

    public withProperties<T>(props: FluentProperties<T, unknown>): FluentComponent<T, TForm> {
        return new FluentComponent({ ...this.spec, properties: props.spec });
    }

    public withFormProperties<T>(props: FluentProperties<T, unknown>): FluentComponent<TProps, T> {
        return new FluentComponent({ ...this.spec, formProperties: props.spec });
    }

    public withDisplayName(displayName: string): FluentComponent<TProps, TForm> {
        return new FluentComponent({ ...this.spec, displayName });
    }

    public withKind(kind: WireComponentKind): FluentComponent<TProps, TForm> {
        return new FluentComponent({ ...this.spec, kind });
    }

    public withIcon(icon: string): FluentComponent<TProps, TForm> {
        return new FluentComponent({ ...this.spec, icon });
    }

    public withinGroup(group: string): FluentComponent<TProps, TForm> {
        return new FluentComponent({ ...this.spec, group });
    }

    public withDescription(description: string): FluentComponent<TProps, TForm> {
        return new FluentComponent({ ...this.spec, description });
    }

    // FIXME: Right now this cannot come before `withProperties` because that will reset `TProps`.  We need to have
    // yet another generic parameter just for the `components` property.
    public withIsContainer(): FluentComponent<TProps & { readonly components: readonly WireComponent[] }, TForm> {
        return new FluentComponent({ ...this.spec, isContainer: true });
    }

    // FIXME: Why is the component name not in the spec?
    // FIXME: Don't use `any`
    public makeHandler(): ComponentHandler<any> {
        if (this.spec.formProperties.propertySpecs.length === 0) {
            // FIXME: Don't use `kind` as the UI component name if we ever use
            // this.
            return makeComponentHandler(this.spec, this.makeUI(defined(this.spec.kind)));
        } else {
            return makeFormComponentHandler(this.spec);
        }
    }
}

type ArrayContentInflator<TProps> = (
    ib: WireInflationBackend,
    desc: ArrayContentDescriptionForProperties<TProps>
) => WireTableComponentHydrator | undefined;

interface FluentArrayContentSpec<TItemProps> {
    readonly format?: WireListComponentFormat;
    readonly specialCase?: ComponentSpecialCaseDescriptor;
    readonly itemProperties: FluentPropertiesSpec;
    readonly componentProperties: FluentPropertiesSpec;
    readonly inflator?: ArrayContentInflator<TItemProps>;
}

export class FluentArrayContent<TItemProps, TComponentProps> {
    constructor(private readonly spec: FluentArrayContentSpec<TItemProps>) {}

    public withItemProperties<T>(
        makeProps: (p: FluentProperties<unknown, TComponentProps>) => FluentProperties<T, TComponentProps>
    ): FluentArrayContent<T, TComponentProps> {
        assert(this.spec.inflator === undefined, "Can't set properties after inflator");
        const props = makeProps(new FluentProperties({ propertySpecs: [], arraySpecs: [], actionSpecs: [] }));
        return new FluentArrayContent({ ...this.spec, inflator: undefined, itemProperties: props.spec });
    }

    public withComponentProperties<T>(props: FluentProperties<T, unknown>): FluentArrayContent<TItemProps, T> {
        assert(this.spec.inflator === undefined, "Can't set properties after inflator");
        return new FluentArrayContent({ ...this.spec, inflator: undefined, componentProperties: props.spec });
    }

    public withFormat(format: WireListComponentFormat): FluentArrayContent<TItemProps, TComponentProps> {
        return new FluentArrayContent({ ...this.spec, format });
    }

    public withSpecialCase(
        specialCase: ComponentSpecialCaseDescriptor
    ): FluentArrayContent<TItemProps, TComponentProps> {
        return new FluentArrayContent({ ...this.spec, specialCase });
    }

    public withInflator(inflator: ArrayContentInflator<TItemProps>): FluentArrayContent<TItemProps, TComponentProps> {
        return new FluentArrayContent({ ...this.spec, inflator });
    }

    public makeHandler(): ArrayContentHandler<ArrayContentDescriptionForProperties<TComponentProps & TItemProps>> {
        const { spec } = this;
        const {
            format,
            specialCase,
            itemProperties: {
                propertySpecs: itemPropertySpecs,
                arraySpecs: itemArraySpecs,
                actionSpecs: itemActionSpecs,
            },
            componentProperties: {
                propertySpecs: componentPropertySpecs,
                arraySpecs: componentArraySpecs,
                actionSpecs: componentActionSpecs,
            },
            inflator,
        } = spec;
        // FIXME: Do the search properties properly - look at the detail screen
        const searchProperties = itemPropertySpecs.filter(p => p.isSearchable).map(p => p.argumentSpec[0]);

        class Handler extends ArrayContentHandlerBase<
            ArrayContentDescriptionForProperties<TComponentProps & TItemProps>
        > {
            public needsCaption(): PropertySection | undefined {
                return undefined;
            }

            public get supportsDynamicFilter(): boolean {
                return true;
            }

            public get specialCaseDescriptor(): ComponentSpecialCaseDescriptor {
                return defined(specialCase);
            }

            public getBasicSearchProperties(): readonly string[] {
                return searchProperties;
            }

            public makeContentForProcessedArray(builder: ComponentBuilder): SerializedComponent {
                // We don't support these for the old component model
                return builder.b.emptyUI;
            }

            public getContentPropertyDescriptors<
                T extends ArrayContentDescriptionForProperties<TComponentProps & TItemProps>
            >(
                getPropertyTable: PropertyTableGetter<T> | undefined,
                insideInlineList: boolean,
                desc: T,
                ccc: ComponentConfiguratorContext,
                mutatingScreenKind: MutatingScreenKind | undefined,
                isDefaultArrayScreen: boolean,
                withTransforms: boolean
            ): readonly PropertyDescriptor[] {
                const superProps = super.getContentPropertyDescriptors(
                    getPropertyTable,
                    insideInlineList,
                    desc,
                    ccc,
                    mutatingScreenKind,
                    isDefaultArrayScreen,
                    withTransforms,
                    undefined
                );
                const componentProps = mapFilterUndefined([...componentPropertySpecs, ...componentArraySpecs], p =>
                    p.descriptorConstructor(desc, undefined, undefined, ccc, mutatingScreenKind, false)
                );
                const itemProps = mapFilterUndefined([...itemPropertySpecs, ...itemArraySpecs], p =>
                    p.descriptorConstructor(desc, desc, getPropertyTable, ccc, mutatingScreenKind, false)
                );
                return [...superProps, ...componentProps, ...itemProps];
            }

            public getActionDescriptors<T extends ArrayContentDescriptionForProperties<TComponentProps & TItemProps>>(
                desc: T,
                getIndirectTable: PropertyTableGetter<T> | undefined,
                schema: SchemaInspector | undefined
            ): readonly ActionPropertyDescriptor<T>[] {
                return [
                    ...makeActionDescriptors<T>(
                        desc,
                        undefined,
                        componentActionSpecs ?? [],
                        undefined,
                        schema,
                        undefined
                    ),
                    ...makeActionDescriptors<T>(
                        desc,
                        undefined,
                        itemActionSpecs ?? [],
                        getIndirectTable,
                        schema,
                        undefined
                    ),
                ];
            }

            public inflateContent<T extends ArrayContentDescriptionForProperties<TComponentProps & TItemProps>>(
                ib: WireInflationBackend,
                desc: T
            ): WireTableComponentHydrator | undefined {
                if (inflator !== undefined) {
                    return inflator(ib, desc);
                }

                const componentGetters = makeGetters(ib, spec.componentProperties, desc);
                const itemGetters = makeGetters(ib, spec.itemProperties, desc);

                // To make TS happy
                assert(format !== ArrayScreenFormat.DataGrid);

                return (thb, chb) => {
                    const data = thb.tableScreenContext;
                    if (data === undefined) return undefined;

                    return {
                        kind: WireComponentKind.List,
                        format: defined(format),
                        componentTitle: "",
                        items: data.map(row => {
                            const rhb = thb.makeHydrationBackendForRow(row);
                            return hydrateGetters<any>(itemGetters, rhb);
                        }),
                        ...definedMap(chb, b => hydrateGetters<any>(componentGetters, b)),
                    } as WireListComponent;
                };
            }
        }

        return new Handler(defined(format));
    }
}

const emptyProperties: FluentPropertiesSpec = { propertySpecs: [], arraySpecs: [], actionSpecs: [] };
export const properties = new FluentProperties(emptyProperties);
export const component = new FluentComponent({
    isContainer: false,
    properties: emptyProperties,
    formProperties: emptyProperties,
});
export const arrayContent = new FluentArrayContent({
    itemProperties: emptyProperties,
    componentProperties: emptyProperties,
});
